import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { FormHelperText, Grid, IconButton, InputAdornment, Tooltip, useMediaQuery } from "@mui/material";
import { Box, Button, TextField, Typography } from '@mui/material';
import "../../style/css/journalForm.css";
import TopHeader from "../../Component/Header/TopHeader";
import { useForm, Controller } from "react-hook-form";
import {
  useSaveForumMutation,
  useUpdateForumMutation,
  useLazyGetForumByIdQuery,
} from "../../app/services/forumServices";
import { useSavePdfForumMutation } from "../../app/services/forumPdfServices";
import placeholder from "../../assets/images/placeholder/placeholder.png"
import {
  selectCurrentScope,
  selectUserId
} from "../../app/auth/authSlice";
import { selectCurrentSig } from "../../app/features/sigSlice";
import { selectCurrentChapterAdmin } from "../../app/features/chapterAdminSlice";
import { useSelector } from "react-redux";
import { useGetAllCategoryMutation } from "../../app/services/categoryService";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { toast } from "react-toastify";
import Spinner from "../../Component/spinner/spinner";
import DeleteIcon from '@mui/icons-material/Delete'
import { useTheme } from "@emotion/react";
import ImageCropModal, { useImageCrop } from '../../Component/ImageCropModal/ImageCropModal';
dayjs.extend(customParseFormat);


function ForumForm() {
  const [loading, setLoading] = useState(false);
  const memberScope = useSelector(selectCurrentScope);
  const userId = useSelector(selectUserId);
  const sigDetails = useSelector(selectCurrentSig);
  const sigId = sigDetails._id;
  const chapterDetails = useSelector(selectCurrentChapterAdmin);
  const chapterId = chapterDetails._id;
  const { forumId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [allCategory, setAllCategory] = useState([]);
  const [saveForum, { isLoading }] = useSaveForumMutation();
  const [savePdfForum] = useSavePdfForumMutation();
  const [getForumById] = useLazyGetForumByIdQuery(forumId);
  const [updateForum] = useUpdateForumMutation();
  const [getAllCategory] = useGetAllCategoryMutation();
  const [pdfErrorMessage, setPdfErrorMessage] = useState("");
  const fileInputRefPdf = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [isPdfFileError, setIsPdfFileError] = useState(false);
  const [selectedDesktopImage, setSelectedDesktopImage] = useState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Crop modal hook — output 365×460 px
  const { cropModal, openCropModal, handleCropApply, closeCropModal } = useImageCrop();

  const onCropDone = (base64) => {
    setSelectedDesktopImage(base64);
    setValue("uploadImage", base64);
    clearErrors("desktopImage");
  };

  // useForm hook for form data handling
  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    reset,
    trigger,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      category: "",
      name: "",
      description: "",
      headline: "",
      uploadImage: "",
      pdfDocumentID: "",
      createdBy: {
        id: "",
        role: "",
      },
      SIGGroupsId: [],
      chaptersId: [],
      uploadPdf: "",
      pdflink: "",
    },
  });

  //Get all category
  const fetchCategory = useCallback(async () => {
    try {
      const response = await getAllCategory({ type: 1 });
      setAllCategory(response.data.data);
    } catch (error) { }
  }, [getAllCategory]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  //Get forum by id
  useEffect(() => {
    if (forumId) {
      setLoading(true);
      getForumById(forumId).then((res) => {
        if (res.data.status) {
          setLoading(false);
          const forum = res.data?.data;
          const data = { ...forum, category: forum?.categoryDetails?.isDeleted === 0 ? forum?.categoryDetails?._id : '' };
          Object.entries(data).forEach(([key, value]) => {
            setValue(key, value);
          });
        }
      });
    }
  }, [forumId, getForumById, setValue]);

  const pdflink = watch("pdflink");

  const getTextWithEllipsis = () => {
    const name = pdfFile?.name || '';  // Get the name dynamically from 'watch'
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;  // Get the viewport width

    // Adjust the maximum number of characters based on the viewport width
    const maxChars = Math.floor(viewportWidth / (isMobile ? 30 : 70));  // Adjust the divisor as needed
    // If name is too long, slice it and append ellipsis
    if (name.length > maxChars) {
      return `${name.slice(0, maxChars)}...`;
    }
    return name;
  };

  //On submit for save and update
  const onSaveForum = (formData) => {
    setLoading(true);
    if (isPdfFileError) {
      setLoading(false);
      return;
    }
    if (pdfFile) {
      const pdfFormData = new FormData();
      pdfFormData.append("uploadPdf", pdfFile);
      savePdfForum(pdfFormData).then((res) => {
        if (res.data.status) {
          if (res.data?.data?.documentID) {
            formData.pdfDocumentID = res.data?.data?.documentID;
          }
          trigger().then((isValid) => {
            if (!forumId) {
              formData.createdBy = {
                id: userId,
                role: memberScope,
              };
            }
            if (isValid) {
              if (!formData.pdfDocumentID) {
                return;
              }
              if (userId && memberScope === "PORTAL_SIG_ADMIN") {
                formData.SIGGroupsId = [sigId];
              } else if (userId && memberScope === "PORTAL_CHAPTER_ADMIN") {
                formData.chaptersId = [chapterId];
              }
              setData([...data, formData]);
              if (forumId) {
                let requestIdAndData;
                requestIdAndData = {
                  id: forumId,
                  data: formData,
                };
                const base64Regex =
                  /^data:image\/(?:png|jpeg|jpg|gif);base64,[\w+/=]+$/;
                const uploadImg = formData?.uploadImage;
                if (base64Regex.test(formData?.uploadImage)) {
                  formData.uploadImage = uploadImg; // No change needed if it's base64
                } else {
                  formData.uploadImage = formData.uploadImage
                    .split("/")
                    .slice(-2)
                    .join("/");
                }
                updateForum(requestIdAndData)
                  .then((res) => {
                    if (res.data.status) {
                      setLoading(false);
                      toast.success(res.data?.message);
                      reset();
                      if (memberScope === "PORTAL_SUPER_ADMIN") {
                        navigate("/admin/forumTable");
                      }
                      if (memberScope === "PORTAL_ADMIN") {
                        navigate("/assistantadmin/forumTable");
                      }
                      if (memberScope === "PORTAL_CHAPTER_ADMIN") {
                        navigate("/chapterAdmin/forumTable");
                      }
                      if (memberScope === "PORTAL_SIG_ADMIN") {
                        navigate("/sigAdmin/forumTable");
                      }
                    } else {
                      toast.error(res.data?.err?.message);
                    }
                  })
                  .catch((error) => {
                    toast.error(error);
                  });
              } else {
                saveForum(formData)
                  .then((res) => {
                    if (res.data?.status) {
                      setLoading(false);
                      toast.success(res.data?.message);
                      reset();
                      if (memberScope === "PORTAL_SUPER_ADMIN") {
                        navigate("/admin/forumTable");
                      }
                      if (memberScope === "PORTAL_ADMIN") {
                        navigate("/assistantadmin/forumTable");
                      }
                      if (memberScope === "PORTAL_CHAPTER_ADMIN") {
                        navigate("/chapterAdmin/forumTable");
                      }
                      if (memberScope === "PORTAL_SIG_ADMIN") {
                        navigate("/sigAdmin/forumTable");
                      }
                    }
                  })
                  .catch((error) => {
                    toast.error(error);
                  });
              }
            }
          });
        }
      });
    } else {
      trigger().then((isValid) => {
        if (isValid) {
          setLoading(true);
          if (!forumId) {
            formData.createdBy = {
              id: userId,
              role: memberScope,
            };
          }
          if (userId && memberScope === "PORTAL_SIG_ADMIN") {
            formData.SIGGroupsId = [sigId];
          } else if (userId && memberScope === "PORTAL_CHAPTER_ADMIN") {
            formData.chaptersId = [chapterId];
          }
          setData([...data, formData]);
          if (forumId) {
            let requestIdAndData;
            requestIdAndData = {
              id: forumId,
              data: formData,
            };
            const base64Regex =
              /^data:image\/(?:png|jpeg|jpg|gif);base64,[\w+/=]+$/;
            const uploadImg = formData?.uploadImage;
            if (base64Regex.test(formData?.uploadImage)) {
              formData.uploadImage = uploadImg; // No change needed if it's base64
            } else {
              formData.uploadImage = formData.uploadImage
                .split("/")
                .slice(-2)
                .join("/");
            }
            updateForum(requestIdAndData)
              .then((res) => {
                if (res.data.status) {
                  setLoading(false);
                  toast.success(res.data?.message);
                  reset();
                  if (memberScope === "PORTAL_SUPER_ADMIN") {
                    navigate("/admin/forumTable");
                  }
                  if (memberScope === "PORTAL_ADMIN") {
                    navigate("/assistantAdmin/forumTable");
                  }
                  if (memberScope === "PORTAL_CHAPTER_ADMIN") {
                    navigate("/chapterAdmin/forumTable");
                  }
                  if (memberScope === "PORTAL_SIG_ADMIN") {
                    navigate("/sigAdmin/forumTable");
                  }
                } else {
                  toast.error(res.data?.err?.message);
                }
              })
              .catch((error) => {
                toast.error(error);
              });
          } else {
            saveForum(formData)
              .then((res) => {
                if (res.data?.status) {
                  setLoading(false);
                  toast.success(res.data?.message);
                  reset();
                  if (memberScope === "PORTAL_SUPER_ADMIN") {
                    navigate("/admin/forumTable");
                  }
                  if (memberScope === "PORTAL_ADMIN") {
                    navigate("/assistantAdmin/forumTable");
                  }
                  if (memberScope === "PORTAL_CHAPTER_ADMIN") {
                    navigate("/chapterAdmin/forumTable");
                  }
                  if (memberScope === "PORTAL_SIG_ADMIN") {
                    navigate("/sigAdmin/forumTable");
                  }
                }
              })
              .catch((error) => {
                toast.error(error);
              });
          }
        }
      });
    }
  };

  // Removed handleDesktopImageChange (using openCropModal instead)

  //Pdf handling
  const handlePdfFileChange = async (event) => {
    const pdfFile = event.target.files[0];
    if (!pdfFile) {
      setPdfFile(null);
      setPdfErrorMessage("");
      setIsPdfFileError(false);
      setValue("pdfDocumentID", "");
      return;
    }
    if (pdfFile.type !== "application/pdf") {
      setError("pdfDocumentID", {
        message: "Only PDF files are allowed!",
      });
      setPdfFile(null);
      setIsPdfFileError(true);
      setValue("pdfDocumentID", "");
      return;
    } else {
      setIsPdfFileError(false);
      setPdfErrorMessage("");
      setPdfFile(pdfFile);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ["application/pdf"];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    if (!file || file.length === 0) {
      // No file selected, so reset the state
      setPdfErrorMessage("");
      setIsPdfFileError(false);
      setValue("pdfDocumentID", "");
      return true; // No file to validate
    }

    const pdfFile = file[0];

    if (pdfFile) {
      return true; // Skip validation
    }

    if (!allowedTypes.includes(pdfFile.type)) {
      setPdfErrorMessage("Only PDFs are allowed!");
      setIsPdfFileError(true);
      return false;
    }

    if (pdfFile.size > maxSize) {
      setPdfErrorMessage("File size should not exceed 5MB!");
      setIsPdfFileError(true);
      return false;
    }

    setPdfErrorMessage("");
    setIsPdfFileError(false);
    setPdfFile(pdfFile);
    return true; // Validation passed
  };

  return (
    <>
      <TopHeader />
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">{forumId ? "Edit Forum" : "Add Forum"}</h2>
        </div>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <Box
          component={"forum"}
          className="d-flex flex-column p-md-5 p-3 overallContactPadding ">
          <div className="text-end w-100 mb-3">
            <Button
              onClick={() => window.history.back()}
              variant="contained"
              className="menus-color"
              style={{ float: "right" }}
            >
              Back
            </Button>
          </div>
          <div>
            <form onSubmit={handleSubmit(onSaveForum)}>
              <Grid container rowSpacing={3} columnSpacing={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    control={control}
                    name="category"
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth>
                        <InputLabel id="text-dark">
                          Category <span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <Select
                          style={{ backgroundColor: "#f0f0f0" }}
                          fullWidth
                          labelId="category-label"
                          id="category"
                          className="bg-white"
                          label={
                            <Typography className="text-dark">
                              Category <span style={{ color: "red" }}>*</span>
                            </Typography>
                          }
                          value={field.value ? field.value : ""}
                          name={field.name}
                          onChange={field.onChange}
                          error={!!fieldState?.error}
                          variant="outlined"
                        >
                          {!isLoading && allCategory?.length > 0 ? (
                            allCategory?.filter(item => item.isDeleted === 0)
                              .map((item, index) => (
                                <MenuItem key={index} value={item._id}>
                                  {item.categoryName}
                                </MenuItem>
                              ))
                          ) : (
                            <MenuItem>Loading....</MenuItem>
                          )}
                        </Select>
                        <FormHelperText className="text-danger">
                          {fieldState?.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                    rules={{ required: "Please Select a Category" }}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="name"
                    label={
                      <Typography className="text-dark">
                        Name<span style={{ color: "red" }}>*</span>
                      </Typography>
                    }
                    variant="outlined"
                    placeholder="Enter Name"
                    {...register("name", { required: "Please Enter a Name" })}
                    error={!!errors?.name}
                    helperText={errors?.name?.message}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    id="headline"
                    fullWidth
                    label={
                      <Typography className="text-dark">
                        HeadLine <span style={{ color: "red" }}>*</span>
                      </Typography>
                    }
                    variant="outlined"
                    placeholder="Enter Headline"
                    {...register("headline", {
                      required: "Please Enter a Headline",
                    })}
                    error={!!errors?.headline}
                    helperText={errors?.headline?.message}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    id="description"
                    fullWidth
                    rows={2}
                    label={
                      <Typography className="text-dark">
                        Description <span style={{ color: "red" }}>*</span>
                      </Typography>
                    }
                    variant="outlined"
                    placeholder="Enter description"
                    {...register("description", { required: "Please enter description" })}
                    error={!!errors?.description}
                    helperText={errors?.description?.message}
                    multiline
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {forumId ? (
                    <>
                      <div className="d-flex justify-content-between px-3 align-items-end">
                        <img
                          src={
                            selectedDesktopImage
                              ? selectedDesktopImage
                              : watch("uploadImage")
                                ? `${watch("uploadImage")}?t=${new Date().getTime()}`
                                : placeholder
                          } // Fetched image from backend with cache busting
                          alt="Forum Img"
                          className="w-25 mx-auto"
                          style={{
                            objectFit: "scale-down",
                          }}
                        />
                        <TextField
                          sx={{ mt: 2 }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ accept: "image/jpeg, image/png" }}
                          id="uploadImage"
                          label={
                            <Typography className="text-dark">
                              Image <span style={{ color: "red" }}>*</span>
                            </Typography>
                          }
                          style={{ display: "none" }}
                          type="file"
                          {...register(`desktopImage`, {
                            validate: (value) => {
                              if (!watch(`uploadImage`)) return "Please select an image";
                              return true;
                            },
                            onChange: (e) => openCropModal(e),
                          })}
                        />
                        <div className="text-center d-flex">
                          <label htmlFor="uploadImage">
                            <Button
                              variant="contained"
                              className="menus-color"
                              component="span"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              Choose Image
                            </Button>
                          </label>
                          <IconButton
                            aria-label="toggle password visibility"
                          >
                            <Tooltip title='Remove File'>
                              <DeleteIcon className="text-danger" onClick={() => setValue('uploadImage', '')} />
                            </Tooltip>
                          </IconButton>
                        </div>
                      </div>
                      {!!errors?.desktopImage && (
                        <FormHelperText error={!!errors?.desktopImage}>
                          {errors?.desktopImage?.message}
                        </FormHelperText>
                      )}
                    </>
                  ) : (
                    <div className="d-flex flex-column w-100 gap-2">
                      {/* Image preview for Add mode */}
                      {(selectedDesktopImage || watch("uploadImage")) && (
                        <div className="d-flex justify-content-center px-3 mb-2">
                          <img
                            src={selectedDesktopImage || watch("uploadImage")}
                            alt="Forum Img"
                            className="w-25 mx-auto"
                            style={{ objectFit: "scale-down" }}
                          />
                        </div>
                      )}
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ accept: "image/jpeg, image/png" }}
                        className="w-100"
                        fullWidth
                        id="uploadImage"
                        error={!!errors?.desktopImage}
                        helperText={errors?.desktopImage?.message}
                        label={
                          <Typography className="text-dark">
                            Image
                          </Typography>
                        }
                        InputProps={{
                          type: 'file',
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                sx={{
                                  marginRight: "-8.5px",
                                }}
                              >
                                <Tooltip title='Remove File'>
                                  <DeleteIcon className="text-danger" onClick={() => { setValue('desktopImage', ''); setValue('uploadImage', ''); setSelectedDesktopImage(null); }} />
                                </Tooltip>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        {...register(`desktopImage`, {
                          validate: (value) => {
                            if (!watch(`uploadImage`)) return "Please select an image";
                            return true;
                          },
                          onChange: (e) => openCropModal(e),
                        })}
                      />
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {forumId && pdflink ? (
                    <div className="border w-100 border-secondary-subtle rounded border-grey d-flex justify-content-between align-items-center px-3 py-2"
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div >
                        {pdfFile ? getTextWithEllipsis() : <a
                          href={pdflink}
                          cursor="pointer"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: "block", marginTop: "10px" }}
                        >
                          View Uploaded PDF
                        </a>}
                      </div>
                      <div>
                        <TextField InputProps={{ type: 'file' }} fullWidth InputLabelProps={{ shrink: true }}
                          label={<Typography className="text-dark text-capitalize">PDF
                          </Typography>}
                          ref={fileInputRefPdf}
                          inputProps={{ accept: ".pdf" }}
                          style={{ display: "none" }}
                          id="pdfFile"
                          type="file"
                          {...register("pdfDocumentID", {
                            validate: validateFile, // Custom validation function
                            onChange: (e) => handlePdfFileChange(e)
                          })}
                          error={!!errors?.pdfDocumentID}
                          helperText={errors?.pdfDocumentID?.message} ></TextField>
                        <label htmlFor="pdfFile" style={{ marginLeft: "10px" }}>
                          <Button
                            variant="contained"
                            className="menus-color"
                            component="span"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Choose PDF File
                          </Button>
                        </label>
                        <IconButton
                          aria-label="toggle password visibility"
                        >
                          <Tooltip title='Remove File'>
                            <DeleteIcon className="text-danger" onClick={() => { setValue('pdfDocumentID', ''); setValue('pdflink', '') }} />
                          </Tooltip>
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <TextField InputProps={{
                      type: 'file',
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            sx={{
                              marginRight: "-8.5px",
                            }}
                          >
                            <Tooltip title='Remove File'>
                              <DeleteIcon className="text-danger" onClick={() => setValue('pdfDocumentID', '')} />
                            </Tooltip>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }} fullWidth InputLabelProps={{ shrink: true }}
                      label={<Typography className="text-dark text-capitalize">PDF
                      </Typography>} ref={fileInputRefPdf}
                      inputProps={{ accept: ".pdf" }}
                      style={{ width: "100%" }}
                      id="pdfFile"
                      type="file"
                      {...register("pdfDocumentID", {
                        validate: validateFile, // Custom validation function,
                        onChange: (e) => handlePdfFileChange(e)
                      })}
                      error={!!errors?.pdfDocumentID}
                      helperText={errors?.pdfDocumentID?.message} ></TextField>
                  )}
                  {pdfErrorMessage && (
                    <FormHelperText error={!!pdfErrorMessage}>
                      {pdfErrorMessage}
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>

              <div className="mt-3 d-flex justify-content-center">
                <>
                  <Button variant="contained" color="secondary" className="rounded-0" type="submit">
                    {forumId ? "Update" : "Submit"}
                  </Button>
                </>
              </div>
            </form>
          </div>
        </Box>
      )}
      {/* Image Crop Modal — crops to 365×460 px */}
      <ImageCropModal
        open={cropModal.open}
        imageSrc={cropModal.src}
        onCancel={closeCropModal}
        onCrop={(base64) => handleCropApply(base64, onCropDone)}
        outputWidth={365}
        outputHeight={460}
        title="Crop Forum Image (365 × 460 px)"
      />
    </>
  );
}

export default ForumForm;
